"use client"

import React, { useEffect, useState } from "react"
import { get, post } from "../utility/fetch"
import { Loader2, Plus, ChevronLeft, ChevronRight } from "lucide-react"
import toast from "react-hot-toast"

export default function UserManagement() {
    const [userType, setUserType] = useState("agency")
    const [users, setUsers] = useState([])
    const [page, setPage] = useState(1)
    const [limit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)
    const [isLoading, setIsLoading] = useState(false)

    const [showForm, setShowForm] = useState(false)
    const [formData, setFormData] = useState({
        email: "",
        username: "",
        password: "",
    })
    const [isSubmitting, setIsSubmitting] = useState(false)

    const fetchUsers = async () => {
        setIsLoading(true)
        try {
            const url =
                userType === "agency"
                    ? `/api/auth/list/agency-user/${page}/${limit}`
                    : `/api/auth/list/citizen-user/${page}/${limit}`

            const res = await get(url)
            if (res?.data?.recordList) {
                setUsers(res.data.recordList)
                setTotalPages(res.data.metadata?.totalPages || 1)
            }
        } catch (error) {
            toast.error("Failed to fetch users")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchUsers()
    }, [userType, page])

    const handleInputChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleCreateUser = async () => {
        setIsSubmitting(true)
        try {
            const res = await post("/api/auth/agency-create", formData)
            toast.success("Agency user created successfully")
            setFormData({ email: "", username: "", password: "" })
            setShowForm(false)
            fetchUsers()
        } catch (err) {
            toast.error("Failed to create agency user")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">User Management</h1>
                {userType === "agency" && (
                    <button
                        onClick={() => setShowForm(!showForm)}
                        className="bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add Agency User
                    </button>
                )}
            </div>

            <div className="flex gap-4">
                <select
                    value={userType}
                    onChange={(e) => {
                        setUserType(e.target.value)
                        setPage(1)
                    }}
                    className="border px-3 py-2 rounded-md"
                >
                    <option value="agency">Agency Users</option>
                    <option value="citizen">Citizen Users</option>
                </select>
            </div>

            {showForm && (
                <div className="border p-4 rounded-md space-y-3 bg-white shadow-sm">
                    <h2 className="font-semibold text-lg">Create New Agency User</h2>
                    <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email"
                        className="border px-3 py-2 rounded-md w-full"
                    />
                    <input
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="Username"
                        className="border px-3 py-2 rounded-md w-full"
                    />
                    <input
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Password"
                        className="border px-3 py-2 rounded-md w-full"
                    />

                    <div className="flex justify-end gap-4 mt-4">
                        <button
                            onClick={() => setShowForm(false)}
                            disabled={isSubmitting}
                            className="bg-red-800 text-white px-4 py-2 rounded-md disabled:opacity-50 flex items-center gap-2"
                        >

                            Cancel
                        </button>

                        <button
                            onClick={handleCreateUser}
                            disabled={isSubmitting}
                            className="bg-primary text-white px-4 py-2 rounded-md disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            Submit
                        </button>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
                </div>
            ) : (
                <div className="space-y-2">
                    {users.map((user) => (
                        <div
                            key={user.id}
                            className="border p-4 rounded-md bg-white shadow-sm flex justify-between items-center"
                        >
                            <div>
                                <p className="font-semibold">{user.username}</p>
                                <p className="text-gray-600 text-sm">{user.email}</p>
                            </div>
                            <div>
                                <span
                                    className={`text-xs px-2 py-1 rounded-full ${user.status === 1
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                        }`}
                                >
                                    {user.status === 1 ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex justify-center items-center gap-4 mt-6">
                <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="border px-3 py-1 rounded disabled:opacity-50"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="border px-3 py-1 rounded disabled:opacity-50"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}
